<template>
    <div class="kanban-board">
        <template v-if="groupValue && groupValue.fieldId">
            <div
                v-for="(groupItem, gIndex) in groupedKanbanData"
                :key="`kanban-group-${gIndex}`"
                class="kanban-group-wrapper"
            >
                <div class="kanban-group-header" @click="toggleGroupCollapse(gIndex)">
                    <i class="material-icons group-icon">
                        {{ isGroupCollapsed(gIndex) ? "chevron_right" : "expand_more" }}
                    </i>
                    <div class="group-label">{{ groupItem.key }} ({{ groupItem.records.length }})</div>

                    <div class="knbn-group-summary-container">
                        <KanbanSummary
                            :records="groupItem.records"
                            :selectedField="kanbanSettings.kanbanField"
                            :kanbanField="getFieldObject(kanbanSettings.kanbanField)"
                        />
                    </div>
                </div>

                <transition name="collapse">
                    <div v-show="!isGroupCollapsed(gIndex)" class="kanban-group-content">
                        <draggable
                            :list="buildColumnsForGroup(columns, groupItem.records)" 
                            class="columns-container"
                            :options="kanbanColumnDragOptions"
                            @end="onColumnOrderChange"
                        >
                            <transition-group tag="div" class="columns-wrapper">
                                <div
                                    v-for="(column, colIndex) in buildColumnsForGroup(columns, groupItem.records)"
                                    :key="columnKey(column, colIndex)"
                                    class="kanban-column"
                                    :data-id="column._id"
                                    :data-index="colIndex"
                                    :data-label="column.label"
                                    :style="getColumnStyles(column._id)"
                                >
                                    <div class="kanban-column-header" :style="getColumnHeaderStyles(column._id)">
                                        {{ column.label }}
                                    </div>

                                    <!-- Kartları sürükle-bırak -->
                                    <draggable
                                        v-model="column.records"
                                        class="kanban-column-body"
                                        :options="kanbanCardDragOptions"
                                        @end="onCardDrop(colIndex, $event)"
                                    >
                                        <transition-group
                                            tag="div"
                                            class="kanban-cards-container"
                                            :data-column-id="column._id"
                                            :data-column-index="colIndex"
                                        >
                                            <KanbanRecordCard
                                                v-for="(record, recIndex) in column.records"
                                                :key="recordKey(record, recIndex)"
                                                :record="record"
                                                :recordIndex="recIndex"
                                                :columnId="column._id"
                                                :columnIndex="colIndex"
                                                :card-fields="kanbanSettings.cardFields"
                                                :table="table"
                                                :selectedRecords="selectedRecords"
                                                :isSelected="isSelected(record._id)"
                                                :isHovered="recordHover === record._id"
                                                @hover="
                                                    (val) => {
                                                        recordHover = val;
                                                    }
                                                "
                                                @toggle-selection="toggleSelection"
                                                :getRecordColor="getRecordColor"
                                                @card-click="onCardClick"
                                            />
                                        </transition-group>
                                    </draggable>
                                </div>
                            </transition-group>
                        </draggable>
                    </div>
                </transition>
            </div>
        </template>

        <!-- Aksi halde, mevcut (orijinal) kodu kullan (gruplama yok) -->
        <template v-else>
            <draggable
                v-model="columns"
                class="columns-container"
                :options="kanbanColumnDragOptions"
                @end="onColumnOrderChange"
            >
                <transition-group tag="div" class="columns-wrapper">
                    <div
                        v-for="(column, colIndex) in columns"
                        :key="columnKey(column, colIndex)"
                        class="kanban-column"
                        :data-id="column._id"
                        :data-index="colIndex"
                        :data-label="column.label"
                        :style="getColumnStyles(column._id)"
                    >
                        <div class="kanban-column-header" :style="getColumnHeaderStyles(column._id)">
                            {{ column.label }}
                        </div>

                        <!-- Kartları sürükle-bırak -->
                        <draggable
                            v-model="column.records"
                            class="kanban-column-body"
                            :options="kanbanCardDragOptions"
                            @end="onCardDrop(colIndex, $event)"
                        >
                            <transition-group
                                tag="div"
                                class="kanban-cards-container"
                                :data-column-id="column._id"
                                :data-column-index="colIndex"
                            >
                                <KanbanRecordCard
                                    v-for="(record, recIndex) in column.records"
                                    :key="recordKey(record, recIndex)"
                                    :record="record"
                                    :record-index="recIndex"
                                    :column-id="column._id"
                                    :column-index="colIndex"
                                    :is-selected="isSelected(record._id)"
                                    :is-hovered="recordHover === record._id"
                                    :card-fields="kanbanSettings.cardFields"
                                    :table="table"
                                    @hover="recordHover = $event"
                                    @card-click="onCardClick"
                                    @toggle-selection="toggleSelection"
                                    :getRecordColor="getRecordColor"
                                />
                            </transition-group>
                        </draggable>
                    </div>
                </transition-group>
            </draggable>
        </template>
    </div>
</template>

<script>
import Vue from "vue";
import draggable from "vuedraggable";
import { mapActions } from "vuex";
import moment from "moment";
import KanbanRecordCard from "./KanbanRecordCard.vue";
import KanbanSummary from "./KanbanSummary.vue";

export default {
    name: "KanbanBoard",
    components: {
        draggable,
        KanbanRecordCard,
        KanbanSummary,
    },
    props: {
        records: {
            type: Array,
            default: function () {
                return [];
            },
        },
        kanbanSettings: {
            type: Object,
            default: function () {
                return {
                    kanbanField: null,
                    cardFields: [],
                };
            },
        },
        table: {
            type: Object,
            required: true,
        },
        selectedRecords: {
            type: Array,
            default: () => [],
        },
        isSelectionModeActive: {
            type: Boolean,
            default: false,
        },
        getRecordColor: {
            type: Function,
            required: true,
        },
        groupValue: {
            type: Object,
         
        },
    },
    data: function () {
        return {
            columns: [],
            recordHover: null,
            collapsedGroups: [],
            kanbanColumnDragOptions: {
                group: "kanban-columns",
                animation: 200,
            },
            kanbanCardDragOptions: {
                group: "kanban-cards",
                animation: 200,
            },
        };
    },
    computed: {
        groupedKanbanData: function () {
            if (!this.groupValue || !this.groupValue.fieldId) {
                return [{ key: "All", records: this.records }];
            }

            var fieldId = this.groupValue.fieldId;
            var showEmpty = this.groupValue.showEmptyGroups;
            var groupMap = {};
            var isDescending = this.groupValue.direction === "desc";
            
            // İlgili alanın fieldType'ını bulalım
            var targetField = this.table.fields.find(function(f) {
                return f._id === fieldId;
            });

            for (var i = 0; i < this.records.length; i++) {
                var rec = this.records[i];
                var val = rec.data[fieldId];

                // Eğer val yoksa (veya 0 değilse) "N/A" olarak gösterelim
                if (!val && val !== 0 && !showEmpty) {
                    val = "N/A";
                }
                if (!val && val !== 0) {
                    val = "N/A";
                }

                // Tarih alanları için semantik gruplama
                if (targetField && targetField.fieldType === "datetime" && val !== "N/A") {
                    val = this.getSemanticDateGroup(val);
                }

                if (!groupMap[val]) {
                    groupMap[val] = [];
                }
                groupMap[val].push(rec);
            }

            var groupArray = [];
            for (var key in groupMap) {
                if (groupMap.hasOwnProperty(key)) {
                    groupArray.push({
                        key: key,
                        records: groupMap[key],
                    });
                }
            }

            // Sıralama
            groupArray.sort(function(a, b) {
                if (isDescending) {
                    if (a.key < b.key) return 1;
                    if (a.key > b.key) return -1;
                } else {
                    if (a.key < b.key) return -1;
                    if (a.key > b.key) return 1;
                }
                return 0;
            });

            return groupArray;
        },
    },
    watch: {
        records: {
            deep: true,
            handler: function () {
                this.buildKanbanColumns();
            },
        },
        kanbanSettings: {
            deep: true,
            handler: function () {
                this.buildKanbanColumns();
            },
        },
    },
    created: function () {
        this.buildKanbanColumns();
    },
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
        }),
        /**
         * Kanban alanına göre sütunları oluşturur ve kartları ilgili sütuna atar.
         */
        buildKanbanColumns: function () {
            var fieldId = this.kanbanSettings.kanbanField;
            if (!fieldId) {
                this.columns = [];
                return;
            }
            var grouping = {};
            for (var i = 0; i < this.records.length; i++) {
                var rec = this.records[i];
                var val = rec.data[fieldId];
                if (!val) {
                    val = "N/A";
                }
                if (!grouping[val]) {
                    grouping[val] = [];
                }
                grouping[val].push(rec);
            }
            var newColumns = [];
            for (var key in grouping) {
                if (grouping.hasOwnProperty(key)) {
                    newColumns.push({
                        _id: key,
                        id: key,
                        label: key,
                        records: grouping[key],
                    });
                }
            }
            // A-Z sıralama
            newColumns.sort((a, b) => {
                return a.label.toString().localeCompare(b.label.toString());
            });
            this.columns = newColumns;
        },
        /**
         * Kart tıklanınca üst seviyeye 'view-record' eventi gönderir.
         */
        onCardClick: function (record) {
            if (this.isSelectionModeActive) {
                this.toggleSelection(record._id);
            } else {
                this.addNewWindow({
                    id: record._id,
                    type: "table-record-viewer",
                    title: this.table.name + " - " + this.$t("modules.recordViewer") + " (" + record.recordId + ")",
                    payload: {
                        tableId: this.table._id,
                        recordId: record._id,
                    },
                    multiple: true,
                    autoClose: false,
                });
            }
        },
        /**
         * Drag bittiğinde sütun sıralaması değiştiğinde çağrılır.
         */
        onColumnOrderChange: function () {
            console.log("Kolon sırası güncellendi:", this.columns);
        },
        /**
         * Kart başka bir sütuna sürüklenip bırakıldığında 'update-record' eventi yayar.
         */
        onCardDrop: function (colIndex, event) {
            console.log("onCardDrop", colIndex, event);
            // Sürüklenen kartın DOM elementini al
            var draggedElement = event.item;
            var toElement = event.to;
            var newColumnId = toElement.getAttribute("data-column-id");

            // Data attribute'lerinden gerekli bilgileri al
            var recordId = draggedElement.getAttribute("data-id");
            var fieldId = this.kanbanSettings.kanbanField;
            var newValue = newColumnId; // Hedef sütunun değeri

            console.log("onCardDrop after", {
                recordId: recordId,
                fieldId: fieldId,
                newValue: newValue,
                draggedElement: draggedElement,
                toElement: toElement,
            });
            // Eğer değer değişmişse güncelleme yap
            if (recordId && fieldId && newValue) {
                this.$emit("update-record", {
                    recordId: recordId,
                    fieldId: fieldId,
                    value: newValue,
                });
            }
        },
        /**
         * Alan ismini döndürür.
         */
        getFieldName: function (fieldId) {
            var field = this.table.fields.find(function (f) {
                return f._id === fieldId;
            });
            if (field) {
                return field.name;
            }
            return fieldId;
        },
        /**
         * İlgili field nesnesini döndürür.
         */
        getFieldObject: function (fieldId) {
            var f = null;
            for (var i = 0; i < this.table.fields.length; i++) {
                if (this.table.fields[i]._id === fieldId) {
                    f = this.table.fields[i];
                    break;
                }
            }
            return f;
        },
        /**
         * Field tipi için uygun ikonu döndürür.
         */
        getFieldIcon: function (field) {
            var map = {
                text: "text_fields",
                "long-text": "subject",
                number: "looks_one",
                currency: "attach_money",
                datetime: "event",
                dropdown: "arrow_drop_down",
                checkbox: "check_box",
                "linked-record": "switch_access_shortcut",
                contact: "person",
                ticket: "confirmation_number",
                task: "assignment",
                event: "event",
                user: "person",
                url: "link",
                email: "email",
                phone: "phone",
                json: "code",
                file: "attachment",
                image: "image",
                video: "videocam",
                audio: "audiotrack",
                color: "palette",
                "automation-button": "smart_button",
            };
            if (map[field.fieldType]) {
                return map[field.fieldType];
            }
            return "info";
        },
        /**
         * Veriyi field tipine göre formatlar.
         */
        getFormattedValue: function (field, value) {
            if (field.fieldType === "datetime" && field.options && field.options.datetime) {
                var timeCfg = field.options.datetime;
                var formatStr = timeCfg.format ? timeCfg.format : "YYYY-MM-DD";
                if (timeCfg.includeTime && timeCfg.timeFormat) {
                    formatStr = formatStr + " " + timeCfg.timeFormat;
                }
                if (value) {
                    return moment(value).format(formatStr);
                }
                return "";
            } else if (field.fieldType === "number") {
                if (field.options && field.options.number && field.options.number.decimalFormat) {
                    // örnek: '00' -> 2 ondalık
                    var decimalFormat = field.options.number.decimalFormat;
                    var floatVal = parseFloat(value);
                    if (isNaN(floatVal)) {
                        return value;
                    }
                    return floatVal.toFixed(decimalFormat.length);
                }
                return value;
            } else if (field.fieldType === "currency") {
                if (field.options && field.options.currency) {
                    var curSymbol = field.options.currency.symbol ? field.options.currency.symbol : "$";
                    var decimalPlace = field.options.currency.decimalFormat
                        ? field.options.currency.decimalFormat
                        : "00";
                    var floatValCur = parseFloat(value);
                    if (isNaN(floatValCur)) {
                        return value;
                    }
                    var curValue = floatValCur.toFixed(decimalPlace.length);
                    return curSymbol + curValue;
                }
                return value;
            }
            return value;
        },
        /**
         * Dropdown'un çoklu seçimi için array'e çevirir.
         */
        getArrayValue: function (value) {
            if (!value) {
                return [];
            }
            if (Array.isArray(value)) {
                return value;
            }
            return [value];
        },
        /**
         * Dropdown'un rengini getirir.
         */
        getDropdownColor: function (optionVal, field) {
            if (!field.options || !field.options.dropdown) {
                return "rgb(210, 216, 243)";
            }
            var dropdown = field.options.dropdown;
            if (dropdown.mode === "dynamic") {
                return this.generateColorFromText(optionVal);
            }
            var list = dropdown.options || [];
            for (var i = 0; i < list.length; i++) {
                if (list[i].value === optionVal) {
                    return list[i].color ? list[i].color : "rgb(210, 216, 243)";
                }
            }
            return "rgb(210, 216, 243)";
        },
        /**
         * Metinden bir renk oluşturur (DropdownCell'deki mantığa benzer).
         */
        generateColorFromText: function (text) {
            var hash = 0;
            for (var i = 0; i < text.length; i++) {
                hash = text.charCodeAt(i) + ((hash << 5) - hash);
            }
            var hue = Math.abs(hash) % 360;
            var saturation = 25 + (Math.abs(hash) % 15);
            var lightness = 85 + (Math.abs(hash) % 10);
            return "hsl(" + hue + ", " + saturation + "%, " + lightness + "%)";
        },
        columnKey: function (column, colIndex) {
            return column._id + "-" + colIndex;
        },
        recordKey: function (record, recIndex) {
            return record._id + "-" + recIndex;
        },
        /**
         * Kolon header stillerini döndürür
         */
        getColumnHeaderStyles(columnId) {
            const field = this.getFieldObject(this.kanbanSettings.kanbanField);
            if (!field || !field.options || !field.options.dropdown) {
                return {};
            }

            const color = this.getDropdownColor(columnId, field);
            return {
                background: `linear-gradient(to right, ${color} 0%, ${this.adjustColor(color, 15)} 100%)`,
                borderBottom: `1px solid ${this.adjustColor(color, -10)}`,
                color: this.getContrastColor(color),
            };
        },

        /**
         * Kolon border stillerini döndürür
         */
        getColumnStyles(columnId) {
            const field = this.getFieldObject(this.kanbanSettings.kanbanField);
            if (!field || !field.options || !field.options.dropdown) {
                return {};
            }

            const color = this.getDropdownColor(columnId, field);
            return {
                borderColor: this.adjustColor(color, -10),
                background: `linear-gradient(to bottom, ${this.adjustColor(color, 45)} 0%, #ffffff 100%)`,
            };
        },

        /**
         * Rengi koyulaştırır veya açıklaştırır
         */
        adjustColor(color, amount) {
            if (color.startsWith("hsl")) {
                const matches = color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
                if (matches) {
                    const h = matches[1];
                    const s = matches[2];
                    const l = Math.max(0, Math.min(100, parseInt(matches[3]) + amount));
                    return `hsl(${h}, ${s}%, ${l}%)`;
                }
            }
            return color;
        },

        /**
         * Arka plan rengine göre kontrast metin rengi döndürür
         */
        getContrastColor(bgColor) {
            if (bgColor.startsWith("hsl")) {
                const matches = bgColor.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
                if (matches) {
                    const lightness = parseInt(matches[3]);
                    return lightness > 65 ? "#000000" : "#FFFFFF";
                }
            }
            return "#000000";
        },

        isSelected(recordId) {
            return this.selectedRecords.includes(recordId);
        },

        toggleSelection(recordId) {
            console.log("toggleSelection", recordId);
            this.$emit("toggle-selection", recordId);
        },

        /**
         * Grupları açıp kapamak için
         */
        toggleGroupCollapse(gIndex) {
            var idx = this.collapsedGroups.indexOf(gIndex);
            if (idx === -1) {
                this.collapsedGroups.push(gIndex);
            } else {
                this.collapsedGroups.splice(idx, 1);
            }
        },

        isGroupCollapsed(gIndex) {
            return this.collapsedGroups.indexOf(gIndex) !== -1;
        },

        /**
         * Belirli bir grup için "columns" dizisini
         * o gruba ait kayıtlara göre filtreleyerek oluşturur.
         */
        buildColumnsForGroup(originalColumns, groupRecords) {
            // Orijinal columns'ı klonlayıp
            // her column içindeki "records" listesini
            // groupRecords içindekilerle eşleyeceğiz.
            var cloned = JSON.parse(JSON.stringify(originalColumns));
            for (var i = 0; i < cloned.length; i++) {
                var col = cloned[i];
                if (!col.records) {
                    col.records = [];
                    continue;
                }
                // Bu sütundaki kayıtları, sadece groupRecords içindekilerle sınırla
                col.records = col.records.filter(function (r) {
                    return groupRecords.some(function (gr) {
                        return gr._id === r._id;
                    });
                });
            }
            // A-Z sıralama
            cloned.forEach(column => {
                column.records.sort((a, b) => {
                    if (a.data && b.data) {
                        const aVal = a.data[this.kanbanSettings.kanbanField] || '';
                        const bVal = b.data[this.kanbanSettings.kanbanField] || '';
                        return aVal.toString().localeCompare(bVal.toString());
                    }
                    return 0;
                });
            });
            return cloned;
        },

        /**
         * Gruplara özel renk dağılımını hesaplamak için (KanbanToolbar'dakine benzer)
         */
        getGroupDistribution(records) {
            // Örnek olarak dropdown benzeri bir renk dağılımını hesaplamak için
            var distribution = {};
            var totalCount = 0;
            for (var i = 0; i < records.length; i++) {
                var rec = records[i];
                var val =
                    this.kanbanSettings && this.kanbanSettings.kanbanField
                        ? rec.data[this.kanbanSettings.kanbanField]
                        : null;
                if (!val) continue;

                if (Array.isArray(val)) {
                    for (var j = 0; j < val.length; j++) {
                        var eachVal = val[j];
                        if (!distribution[eachVal]) {
                            distribution[eachVal] = 0;
                        }
                        distribution[eachVal]++;
                        totalCount++;
                    }
                } else {
                    if (!distribution[val]) {
                        distribution[val] = 0;
                    }
                    distribution[val]++;
                    totalCount++;
                }
            }

            var resultArray = [];
            var options =
                this.kanbanField && this.kanbanField.options ? this.kanbanField.options.dropdown.options || [] : [];

            for (var key in distribution) {
                if (distribution.hasOwnProperty(key)) {
                    var count = distribution[key];
                    var colorVal = this.findOptionColor(key, options) || this.generateColorFromText(key);
                    var ratio = count / totalCount;
                    resultArray.push({
                        value: key,
                        count: count,
                        color: colorVal,
                        flexValue: ratio * 10,
                        ratio: ratio,
                    });
                }
            }
            return resultArray.sort(function (a, b) {
                return b.count - a.count;
            });
        },

        getDropdownTooltip(dist) {
            var percentage = (dist.ratio * 100).toFixed(2);
            return dist.value + " | Count: " + dist.count + " | Percentage: " + percentage + "%";
        },

        findOptionColor(value, options) {
            var opt = options.find(function (o) {
                return o.value === value;
            });
            return opt ? opt.color : null;
        },

        /**
         * Tarihsel değerleri semantik gruba ayırır:
         * - Yesterday, Today, Tomorrow, This Week
         * - Diğerleri: "MMMM YYYY" formatında (Örneğin, "Ekim 2023")
         */
        getSemanticDateGroup: function(dateValue) {
            var d = moment(dateValue);
            var now = moment();

            if (d.isSame(now, "day")) {
                return "Today";
            } else if (d.isSame(now.clone().subtract(1, "day"), "day")) {
                return "Yesterday";
            } else if (d.isSame(now.clone().add(1, "day"), "day")) {
                return "Tomorrow";
            } else if (d.isSame(now, "week")) {
                return "This Week";
            }
            return d.format("MMMM YYYY");
        },
    },
};
</script>

<style scoped lang="scss">
.kanban-board {
    padding: 10px;
    width: 100%;
    overflow-x: auto;
    height: 100%;

    .columns-container {
        height: 100%;
    }

    .columns-wrapper {
        display: flex;
        gap: 16px;
        min-height: 200px;
        height: 100%;
        //height: 100%;
        //max-height: calc(100% - 30px);
    }

    .kanban-column {
        background: #fff;
        border: 1px solid #d9d9d9;
        border-radius: 4px;
        min-width: 300px;
        display: flex;
        flex-direction: column;
        border-width: 2px;
        height: max-content;
        min-height: 200px;

        .kanban-column-header {
            background-color: #fafafa;
            padding: 8px;
            font-weight: bold;
            border-bottom: 1px solid #d9d9d9;
            color: inherit;
            transition: background-color 0.3s ease;
        }

        .kanban-column-body {
            padding: 8px;
            display: flex;
            flex-direction: column;
            gap: 8px;
            flex-grow: 1;
            border-radius: 0 0 4px 4px;

            .kanban-cards-container {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }
        }
    }

    .kanban-card {
        background-color: #fff;
        border: 1px solid #e8e8e8;
        border-radius: 4px;
        padding: 10px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
        transition: background-color 0.2s ease-in-out;
        cursor: pointer;
        position: relative;

        &.hovered-card {
            background-color: #f5f5f5;
        }

        &.selected-card {
            background-color: #e6f7ff;
            border-color: #1890ff;
        }

        .card-selection-checkbox {
            position: absolute;
            top: 0px;
            left: 0px;
            z-index: 1;
            cursor: pointer;

            .material-icons {
                font-size: 20px;
                transition: all 0.3s ease;

                &:first-child {
                    color: #1890ff;
                }

                &:last-child {
                    color: rgba(0, 0, 0, 0.25);

                    &:hover {
                        color: #1890ff;
                    }
                }
            }
        }

        &.selected-card .kanban-card-field {
            //padding-left: 24px;
        }

        .kanban-card-field {
            margin-bottom: 4px;
            display: flex;
            align-items: center;

            i {
                margin-right: 4px;
            }

            label {
                font-weight: 600;
                margin-right: 4px;
                margin-bottom: 0;
            }

            .selected-options {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;

                .option-chip {
                    display: inline-block;
                    padding: 2px 6px;
                    border-radius: 16px;
                    margin-right: 4px;
                    cursor: pointer;
                }
            }
        }
    }

    .kanban-group-wrapper {
        margin-bottom: 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        overflow: hidden;

        .kanban-group-header {
            display: flex;
            align-items: center;
            padding: 8px;
            background-color: #f3f3f3;
            cursor: pointer;
            border-bottom: 1px solid #ccc;

            .group-icon {
                margin-right: 8px;
            }

            .group-label {
                font-weight: bold;
                flex: 1;
            }

            .knbn-group-summary-container {
                margin-left: auto;
                display: flex;
                align-items: center;
                width: 200px;
            }
        }

        .kanban-group-content {
            padding: 10px;
            background-color: #fff;
            overflow-x: scroll;
        }
    }

    // collapse animasyonu
    .collapse-enter-active,
    .collapse-leave-active {
        transition: all 0.3s ease;
    }

    .collapse-enter,
    .collapse-leave-to {
        height: 0;
        opacity: 0;
        overflow: hidden;
    }
}
</style>
